import React, { memo, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Redirect, Route, Switch } from 'react-router-dom';
import { FragnoTabs } from 'fragno/react';
import style from '../style/pages/WalletProfile.module.scss';
import WalletHeader from '../components/profile/WalletHeader';
import WalletPersonalProfile from '../components/profile/WalletPersonalProfile';
import WalletUsWorkProfile from '../containers/WalletUsWorkProfile';
import WalletEmails from '../containers/WalletEmails';
import WalletPreferences from '../containers/WalletPreferences';
import { fetchCountryWorkProfile } from '../actions/countryWorkProfile';
import { trackPageView } from '../utils/analytics';
import Four04 from './Four04';

const WalletProfile = ({ location = {}, history = {} }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const emailsFetching = useSelector((state) => state.walletEmails.isFetching);
  const countryWorkProfileEnabled = useSelector(
    (state) => state.featureFlags.countryWorkProfile,
  );
  // present is null until the fetch resolves, then true/false.
  const { present: hasCountryWorkProfile, isFetching: isFetchingWorkProfile } =
    useSelector((state) => state.countryWorkProfile);

  // The "U.S. Work" tab is gated on a resolved profile (not just the flag), so
  // fetch presence once the feature is enabled and it isn't already known.
  useEffect(() => {
    if (
      countryWorkProfileEnabled &&
      !isFetchingWorkProfile &&
      hasCountryWorkProfile === null
    ) {
      dispatch(fetchCountryWorkProfile());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, countryWorkProfileEnabled]);

  const { hash, pathname } = location;
  const workProfileResolved = hasCountryWorkProfile !== null;
  // Show the tab when a profile exists, and also while a deep-link onto the
  // U.S. Work route is still resolving, so it doesn't flash the Personal layout.
  const showUsWork =
    countryWorkProfileEnabled &&
    (hasCountryWorkProfile === true ||
      (!workProfileResolved && pathname === '/profile/uswork'));
  const routes = [
    { children: t('profile.tabPersonal'), to: '/profile/personal' },
    { children: t('profile.tabUsWork'), to: '/profile/uswork' },
  ];
  // Tracks which hash value we've already scrolled to so re-renders after
  // the fetch resolves don't keep firing scrolls.
  const scrolledFor = useRef(null);

  useEffect(() => {
    if (!countryWorkProfileEnabled) {
      trackPageView('/wallet/profile');
    } else if (showUsWork && pathname === '/profile/uswork') {
      trackPageView('/wallet/profile/uswork');
    } else {
      trackPageView('/wallet/profile/personal');
    }
    // showUsWork is intentionally not a dep: it's already true at first paint on
    // the U.S. Work route, and adding it would double-fire on the Personal route.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryWorkProfileEnabled, pathname]);

  // Scroll to the section named by the URL hash (e.g. /profile/personal/#emails from
  // the Help page links). Wait until the WalletEmails fetch resolves so the
  // table content is in the DOM before we scroll — otherwise the card
  // height is just the title and we land above the rows.
  useEffect(() => {
    if (!hash || emailsFetching) return undefined;
    if (scrolledFor.current === hash) return undefined;
    scrolledFor.current = hash;
    const id = hash.slice(1);
    const raf = requestAnimationFrame(() => {
      document
        .getElementById(id)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => cancelAnimationFrame(raf);
  }, [hash, emailsFetching]);

  const activeTabIndex = Math.max(
    routes.findIndex((route) => route.to === pathname),
    0,
  );

  const handleTabChange = (e) => {
    const nextRoute = routes[e.detail.selectedIndex]?.to;
    if (nextRoute && nextRoute !== pathname) {
      history.push(nextRoute);
    }
  };

  const renderPersonalProfile = () => (
    <>
      <WalletPersonalProfile user={user} />
      <WalletEmails />
      <WalletPreferences />
    </>
  );

  const renderUsWorkProfile = () => <WalletUsWorkProfile />;

  if (showUsWork) {
    return (
      <main className={style.content}>
        <WalletHeader user={user} />

        <div className={style.tabsWrapper}>
          <FragnoTabs
            tabLabels={routes.map((route) => route.children).join('|')}
            selectedIndex={activeTabIndex}
            onFragnoTabsChange={handleTabChange}
            size="md"
          />
        </div>

        <Switch>
          <Route
            exact
            path="/profile"
            render={({ location: currentLocation }) => (
              <Redirect
                to={{
                  pathname: '/profile/personal',
                  hash: currentLocation.hash,
                }}
              />
            )}
          />
          <Route exact path="/profile/personal">
            {renderPersonalProfile()}
          </Route>
          <Route exact path="/profile/uswork">
            {renderUsWorkProfile()}
          </Route>
          <Route component={Four04} />
        </Switch>
      </main>
    );
  }

  return (
    <main className={style.content}>
      <WalletHeader user={user} />
      {renderPersonalProfile()}
    </main>
  );
};

export default memo(WalletProfile);
